/**
 * API client. Two server auth modes:
 *  - password (production): an httpOnly session cookie the browser sends by
 *    itself. Nothing to attach here, and nothing readable from JavaScript.
 *  - stub (local dev): the selected username travels as x-agio-user.
 * Either way the SERVER decides what rows exist (Postgres RLS) — nothing here
 * is security.
 */
import type { UploadMessage } from '@agio/core';

export function currentUsername(): string | null {
  return localStorage.getItem('agio_user');
}

export function setCurrentUsername(u: string | null): void {
  if (u) localStorage.setItem('agio_user', u);
  else localStorage.removeItem('agio_user');
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    /** Rejected-upload only: the concrete next action, and where the problem is. */
    public fix?: string,
    public where?: string,
    /** Rejected-upload only: every problem found, each with its facts and steps. */
    public messages?: UploadMessage[],
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const user = currentUsername();
  const res = await fetch(`/api${path}`, {
    ...init,
    // The session cookie rides along on its own; it is httpOnly by design.
    credentials: 'same-origin',
    headers: {
      ...(init.headers ?? {}),
      ...(user ? { 'x-agio-user': user } : {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(res.status, body.error ?? res.statusText, body.code, body.fix, body.where, body.messages);
  }
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', body: form }),
  /**
   * Binary fetch, for embedding a file in the page.
   *
   * A plain <object src="/api/..."> cannot carry the x-agio-user header that
   * local dev mode authenticates with, so it 401s there while working in
   * production off the cookie. Going through here behaves the same in both.
   */
  blob: async (path: string): Promise<Blob> => {
    const user = currentUsername();
    const res = await fetch(`/api${path}`, {
      credentials: 'same-origin',
      headers: user ? { 'x-agio-user': user } : {},
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: res.statusText }));
      throw new ApiError(res.status, body.error ?? res.statusText, body.code);
    }
    return res.blob();
  },
};
